import { useTheme } from '../context/ThemeContext';
import { Moon, Sun, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function RefundPage() {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full px-4 py-3 bg-background/95 backdrop-blur border-b border-border flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-secondary rounded-full transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-semibold">Refund Policy</h1>
        <button
          onClick={toggleTheme}
          className="p-2 hover:bg-secondary rounded-full transition-colors"
        >
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <p className="text-muted-foreground text-sm mb-6">Last updated: January 9, 2026</p>

          <h2 className="text-xl font-bold mt-6 mb-3">Subscription Refund Policy</h2>
          <p>
            FlipValue offers subscription-based services (Basic and Pro plans) as well as a one-time Lifetime Pro purchase with the following refund terms:
          </p>

          <h3 className="text-lg font-semibold mt-5 mb-2">Free Trial Period</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Basic Plan:</strong> 3-day free trial</li>
            <li><strong>Pro Plan:</strong> 7-day free trial</li>
          </ul>
          <p>
            You will not be charged during the trial period. You may cancel at any time before the trial ends to avoid being charged. Note: Users who sign up using a referral code for a free month do not receive an additional trial period.
          </p>

          <h3 className="text-lg font-semibold mt-5 mb-2">Lifetime Pro Purchase</h3>
          <p>
            Lifetime Pro is a one-time purchase that grants permanent Pro-tier access. Due to the nature of this offer:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Lifetime Pro purchases are <strong>non-refundable</strong></li>
            <li>The purchase grants immediate and permanent access to Pro features</li>
            <li>Availability is limited and the offer may be discontinued at any time</li>
          </ul>

          <h3 className="text-lg font-semibold mt-5 mb-2">Cancellation</h3>
          <p>
            You may cancel your subscription at any time through your account settings or by contacting us. Upon cancellation:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>You will retain access to paid features until the end of your current billing period</li>
            <li>Your subscription will not renew for the next billing cycle</li>
            <li>No partial refunds are provided for unused time within a billing period</li>
          </ul>

          <h3 className="text-lg font-semibold mt-5 mb-2">Refund Eligibility</h3>
          <p>Refunds for monthly/annual subscriptions may be issued in the following circumstances:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Technical Issues:</strong> If you experience significant technical problems that prevent you from using the service and we are unable to resolve them</li>
            <li><strong>Duplicate Charges:</strong> If you were charged multiple times for the same subscription period</li>
            <li><strong>Unauthorized Charges:</strong> If a charge was made without your authorization</li>
          </ul>

          <h3 className="text-lg font-semibold mt-5 mb-2">How to Request a Refund</h3>
          <p>
            To request a refund, please contact us at <a href="mailto:support@flipvalue.app" className="text-primary hover:underline">support@flipvalue.app</a> with:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Your account email address</li>
            <li>Date of the charge</li>
            <li>Reason for the refund request</li>
          </ul>
          <p>
            We will review your request and respond within 3-5 business days. Approved refunds will be processed to your original payment method within 5-10 business days.
          </p>

          <h3 className="text-lg font-semibold mt-5 mb-2">Non-Refundable Items</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Lifetime Pro purchases</strong></li>
            <li>Partial months or unused subscription time</li>
            <li>Subscription fees after the 14-day refund window (from first payment)</li>
            <li>Charges for previous billing periods</li>
            <li>Referral credits and rewards</li>
          </ul>

          <h3 className="text-lg font-semibold mt-5 mb-2">Regional Considerations</h3>
          <p>
            If you are located in a region with specific consumer protection laws (such as the EU), you may have additional rights. Please contact us if you believe local laws entitle you to a refund not covered by this policy.
          </p>

          <h3 className="text-lg font-semibold mt-5 mb-2">Contact Us</h3>
          <p>
            If you have questions about our refund policy, please contact us:
          </p>
          <ul className="list-none pl-0 space-y-1">
            <li>Email: <a href="mailto:support@flipvalue.app" className="text-primary hover:underline">support@flipvalue.app</a></li>
          </ul>

          <div className="mt-8 p-4 bg-secondary/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> This refund policy is subject to change. We recommend reviewing it periodically. Continued use of our service constitutes acceptance of any updates.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
